/// <reference path="DocumentExtractionLineCell.ts" />

// What DLInsightsBotServer read on a supplier document and what it proposed, from the info icon of the
// document's top bar: the document and its supplier match, the issues to review, the totals
// read against the lines on the document, and the detected lines with their match.
// Informative only: the lines are added from the detail view itself.

class DocumentExtractionInfoViewController extends MUIViewController
{
    static newInstance(): DocumentExtractionInfoViewController {
        let vc = new DocumentExtractionInfoViewController("document-extraction-info-view");
        vc.initWithResource("layout/document_extraction/DocumentExtractionInfoView.html");
        return vc;
    }

    get preferredContentSize() {
        return new MIOSize(880, 760);
    }

    session:DocumentExtractionSession = null;

    private closeButton:MUIButton = null;
    private rescanButton:MUIButton = null;
    private docNumberLabel:MUILabel = null;
    private docDateLabel:MUILabel = null;
    private supplierNameLabel:MUILabel = null;
    private supplierVatLabel:MUILabel = null;
    private supplierArrowLabel:MUILabel = null;
    private supplierMatchLabel:MUILabel = null;
    private optionsView:MUIView = null;
    private pricesIncludeTaxSwitch:MUISwitchButton = null;
    private groupLinesSwitch:MUISwitchButton = null;
    private groupLinesConceptDropdown:MUIButton = null;
    private issuesView:MUIView = null;
    private issuesTitleLabel:MUILabel = null;
    private issuesListView:MUIView = null;
    private totalsView:MUIView = null;
    private totalsTableView:MUIView = null;
    private linesView:MUIView = null;
    private linesTitleLabel:MUILabel = null;
    private linesTableView:MUIView = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.closeButton = MUIOutlet(this, "close-btn", "MUIButton");
        this.closeButton.setAction(this, function(this:DocumentExtractionInfoViewController){
            this.dismissViewController(true);
        });

        this.rescanButton = MUIOutlet(this, "rescan-btn", "MUIButton");
        this.rescanButton.setAction(this, function(this:DocumentExtractionInfoViewController){
            this.rescan();
        });

        this.docNumberLabel = MUIOutlet(this, "doc-number-lbl", "MUILabel");
        this.docDateLabel = MUIOutlet(this, "doc-date-lbl", "MUILabel");
        this.supplierNameLabel = MUIOutlet(this, "supplier-name-lbl", "MUILabel");
        this.supplierVatLabel = MUIOutlet(this, "supplier-vat-lbl", "MUILabel");
        this.supplierArrowLabel = MUIOutlet(this, "supplier-arrow-lbl", "MUILabel");
        this.supplierMatchLabel = MUIOutlet(this, "supplier-match-lbl", "MUILabel");
        this.optionsView = MUIOutlet(this, "options-view", "MUIView");
        this.pricesIncludeTaxSwitch = MUIOutlet(this, "prices-include-tax-sb", "MUISwitchButton");
        if (this.pricesIncludeTaxSwitch) this.pricesIncludeTaxSwitch.setOnChangeValue(this, function(this:DocumentExtractionInfoViewController, control:MUISwitchButton, value:boolean){
            this.saveOptions({ "pricesIncludeTax": value });
        });
        this.groupLinesSwitch = MUIOutlet(this, "group-lines-sb", "MUISwitchButton");
        if (this.groupLinesSwitch) this.groupLinesSwitch.setOnChangeValue(this, function(this:DocumentExtractionInfoViewController, control:MUISwitchButton, value:boolean){
            this.saveOptions({ "groupLines": value });
        });
        this.groupLinesConceptDropdown = MUIOutlet(this, "group-lines-concept-dd", "MUIButton");
        if (this.groupLinesConceptDropdown) this.groupLinesConceptDropdown.setAction(this, function(this:DocumentExtractionInfoViewController){
            let vc = AppHelper.sharedInstance().selectViewController("ConceptItem", "name", true, false, null, this, null) as SelectEntityViewController;
            AppHelper.sharedInstance().allowAddingConceptItem(vc, (concept:ConceptItem) => this.didSelectObjectFromSelectViewController(vc, concept));
            vc.modalPresentationStyle = MUIModalPresentationStyle.Popover;
            let pc = vc.popoverPresentationController;
            pc.sourceRect = this.groupLinesConceptDropdown.frame;
            pc.sourceView = this.groupLinesConceptDropdown;
            this.presentViewController(vc, true);
        });
        this.issuesView = MUIOutlet(this, "issues-view", "MUIView");
        this.issuesTitleLabel = MUIOutlet(this, "issues-title-lbl", "MUILabel");
        this.issuesListView = MUIOutlet(this, "issues-list-view", "MUIView");
        this.totalsView = MUIOutlet(this, "totals-view", "MUIView");
        this.totalsTableView = MUIOutlet(this, "totals-table-view", "MUIView");
        this.linesView = MUIOutlet(this, "lines-view", "MUIView");
        this.linesTitleLabel = MUIOutlet(this, "lines-title-lbl", "MUILabel");
        this.linesTableView = MUIOutlet(this, "lines-table-view", "MUIView");
    }

    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);
        if (this.session != null) this.session.stateDidChange = () => this.reload();
        this.reload();
    }

    viewWillDisappear(animated?:boolean){
        super.viewWillDisappear(animated);
        if (this.session != null) this.session.stateDidChange = null;
    }

    // Reads the document again: a new engine run on the same files, for a reading that missed
    // something (the totals, a line). An invoice goes to the list's processing screen meanwhile.
    private rescan(){
        if (this.session == null || this.session.isRescanning) return;
        this.session.rescan((ok:boolean, message:string) => {
            if (ok && this.session.isInvoice) { this.dismissViewController(true); return; }
            this.reload();
            if (!ok) AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), message);
        });
        this.updateRescanButton();
    }

    // The supplier's reading options: written on the server, which re-derives the reading with
    // them (the rows rebuild from it); the switches follow the session.
    private saveOptions(values:any){
        if (this.session == null) return;
        this.session.saveOptions(values, (ok:boolean) => {
            if (!ok) AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_OPTIONS_COULD_NOT_BE_SAVED", "The options could not be saved."));
            this.reload();
        });
    }

    private renderOptions(){
        let session = this.session;
        let options = session.readingOptions ?? {};
        if (this.optionsView) this.optionsView.hidden = !session.isInvoice && session.readingOptions == null;
        if (this.pricesIncludeTaxSwitch) this.pricesIncludeTaxSwitch.on = options["pricesIncludeTax"] == true;
        if (this.groupLinesSwitch) this.groupLinesSwitch.on = options["groupLines"] == true;
        if (this.groupLinesConceptDropdown) {
            let concept = session.groupLinesConcept;
            this.groupLinesConceptDropdown.title = concept != null ? DocumentExtractionLineCell.escape(concept.name) : MIOLocalizeString("CUSTOM_LINE_CONCEPT", "CUSTOM LINE CONCEPT");
        }
    }

    // Select entity view controller (the custom line concept)

    fetchRequestForController(controller:SelectEntityViewController) : MIOFetchRequest {
        if (controller.identifier == "ConceptItem") {
            let sd = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
            return DBHelper.listFetchRequestWithEntityName("ConceptItem", sd, MIOPredicate.predicateWithFormat("deletedAt = null"));
        }
        return null;
    }

    didSelectObjectFromSelectViewController(controller:SelectEntityViewController, item:MIOManagedObject) : boolean {
        if (controller.identifier == "ConceptItem") {
            let concept = item as ConceptItem;
            this.saveOptions({ "groupLinesConceptID": concept != null ? concept.identifier.toUpperCase() : "" });
        }
        return true;
    }

    private updateRescanButton(){
        let scanning = this.session != null && (this.session.isRescanning || this.session.state == "scanning");
        this.rescanButton.enabled = !scanning;
        this.rescanButton.setTitle(scanning ? MIOLocalizeString("READING_THE_DOCUMENT", "READING THE DOCUMENT...") : MIOLocalizeString("RE_SCAN", "RE-SCAN"));
    }

    // MARK: rendering

    private reload(){
        let session = this.session;
        if (session == null || !this.viewIsLoaded) return;
        this.updateRescanButton();
        let esc = DocumentExtractionLineCell.escape;

        // being read, or the reading failed (a supplier note reaches this view in those states)
        if (session.state == "scanning" || session.state == "failed") {
            let failed = session.state == "failed";
            this.docNumberLabel.text = failed ? MIOLocalizeString("THE_READING_FAILED", "THE READING FAILED") : MIOLocalizeString("READING_THE_DOCUMENT", "READING THE DOCUMENT...");
            this.docDateLabel.text = "";
            this.supplierNameLabel.text = failed ? esc(session.ocrError ?? MIOLocalizeString("THE_DOCUMENT_COULD_NOT_BE_READ", "The document could not be read.")) : MIOLocalizeString("THE_LINES_APPEAR_WHEN_THE_READING_ARRIVES", "The lines appear on the document when the reading arrives.");
            this.supplierVatLabel.text = "";
            this.supplierArrowLabel.text = "";
            this.supplierMatchLabel.hidden = true;
            if (this.optionsView) this.optionsView.hidden = true;
            this.issuesView.hidden = true;
            this.totalsView.hidden = true;
            this.linesView.hidden = true;
            return;
        }

        let ex = session.reading ?? {};
        let proposal = session.readingProposal;
        this.renderDocument(ex, proposal);
        this.renderOptions();
        let issues = this.issues(ex, proposal);
        this.renderIssues(issues);
        this.renderTotals(ex);
        this.renderLines(ex);
    }

    // 1. the document and its supplier

    private renderDocument(ex:any, proposal:any){
        let esc = DocumentExtractionLineCell.escape;
        let type = ex["documentType"] ?? "";
        let typeLabel = type == "delivery_note" ? MIOLocalizeString("DELIVERY_NOTE", "Delivery note")
                      : type == "credit_note" ? MIOLocalizeString("CREDIT_NOTE", "Credit note")
                      : type == "purchase_invoice" || type == "invoice" ? MIOLocalizeString("INVOICE", "Invoice")
                      : MIOLocalizeString("DOCUMENT", "Document");
        this.docNumberLabel.text = typeLabel + (ex["number"] ? " " + esc(ex["number"]) : "");
        let date = this.formattedDate(ex["date"]);
        this.docDateLabel.text = date != null ? "· " + esc(date) : "";

        let supplier = ex["supplier"] ?? {};
        this.supplierNameLabel.text = esc(supplier["name"] ?? MIOLocalizeString("SUPPLIER_NOT_READ", "Supplier not read"));
        this.supplierVatLabel.text = supplier["taxId"] ? MIOLocalizeString("VAT", "VAT") + " " + esc(supplier["taxId"]) : "";

        let match = proposal != null ? proposal["supplier"] : null;
        let layer = this.supplierMatchLabel.layer as HTMLElement;
        layer.classList.remove("unknown");
        if (match == null) {
            this.supplierArrowLabel.text = "";
            this.supplierMatchLabel.hidden = true;
            return;
        }
        this.supplierArrowLabel.text = "→";
        this.supplierMatchLabel.hidden = false;
        if (match["status"] == "unknown" || match["name"] == null) {
            this.supplierMatchLabel.text = MIOLocalizeString("UNKNOWN_SUPPLIER", "Unknown supplier");
            layer.classList.add("unknown");
        }
        else this.supplierMatchLabel.text = esc(match["name"]);
    }

    private formattedDate(value:any) : string {
        if (value == null || String(value).length == 0) return null;
        let parts = String(value).split("-");
        if (parts.length != 3) return String(value);
        let date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (isNaN(date.getTime())) return String(value);
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        return ad.dateFormatter.stringFromDate(date);
    }

    // 2. the issues to review: the totals against the lines, the taxes that are none of ours,
    // the readings' findings and the matcher's warnings about the document (line warnings show
    // on the lines themselves)

    private issues(ex:any, proposal:any) : { severity:string, title:string, detail:string }[] {
        let esc = DocumentExtractionLineCell.escape;
        let session = this.session;
        let money = this.money;
        let issues:{ severity:string, title:string, detail:string }[] = [];

        let totals = ex["totals"] ?? {};
        let readTotal = this.number(totals["total"]);
        if (readTotal != null) {
            let now = this.linesTotals();
            if (Math.abs(now.total - readTotal) >= 0.015) {
                issues.push({ severity: "error", title: MIOLocalizeString("TOTALS_DONT_MATCH", "Totals don't match."),
                              detail: MIOLocalizeString("DOCUMENT_TOTAL_IS", "Document total is") + " " + money(readTotal) + ", " + MIOLocalizeString("INVOICE_IS_CURRENTLY", "invoice is currently") + " " + money(now.total) + "." });
            }
        }

        let rates:string[] = [];
        for (let t of (ex["taxSummary"] ?? []) as any[]) if (t["rate"] != null) rates.push(String(t["rate"]));
        for (let line of (ex["lines"] ?? []) as any[]) if (line["taxRate"] != null) rates.push(String(line["taxRate"]));
        let unknown = rates.filter((r, i) => rates.indexOf(r) == i && session.venueTaxForRate(r) == null);
        for (let rate of unknown) {
            issues.push({ severity: "error", title: MIOLocalizeString("UNKNOWN_TAX_RATE", "Unknown tax rate."),
                          detail: "IVA " + esc(rate) + "% " + MIOLocalizeString("IS_NOT_ONE_OF_YOUR_TAXES", "is not one of your taxes.") });
        }

        for (let g of session.readingGates) {
            if (g["severity"] == "info") continue;
            issues.push({ severity: g["severity"] == "blocking" ? "error" : "warning", title: this.issueTitle(g["code"] ?? g["id"]), detail: esc(g["message"] ?? "") });
        }
        if (proposal != null) for (let w of (proposal["warnings"] ?? []) as any[]) {
            if (w["severity"] == "info" || w["lineIndex"] != null) continue;
            issues.push({ severity: w["severity"] == "blocking" ? "error" : "warning", title: this.issueTitle(w["code"]), detail: esc(w["message"] ?? "") });
        }
        return issues;
    }

    private issueTitle(code:string) : string {
        switch (code) {
            case "no_lines": return MIOLocalizeString("NO_LINES_READ", "No lines read.");
            case "no_total": return MIOLocalizeString("NO_TOTAL_READ", "No total read.");
            case "no_number": return MIOLocalizeString("NO_NUMBER_READ", "No document number read.");
            case "no_supplier": return MIOLocalizeString("NO_SUPPLIER_READ", "No supplier read.");
            case "total": case "sum_base": case "tax_summary": case "tax_base_lines": case "line_base": case "line_tax": return MIOLocalizeString("AMOUNTS_DONT_ADD_UP", "Amounts on the document don't add up.");
            case "supplier_tax_id_invalid": return MIOLocalizeString("SUPPLIER_TAX_ID_LOOKS_WRONG", "Supplier tax id looks wrong.");
            case "date_format": case "date_future": case "date_old": return MIOLocalizeString("DATE_LOOKS_WRONG", "Date looks wrong.");
            case "currency": return MIOLocalizeString("CURRENCY_IS_NOT_EURO", "Currency is not euro.");
            case "tax_rate_unknown": return MIOLocalizeString("UNKNOWN_TAX_RATE", "Unknown tax rate.");
            case "tax_rate_missing": return MIOLocalizeString("VAT_MISSING_ON_A_LINE", "VAT missing on a line.");
            case "note_not_registered": return MIOLocalizeString("DELIVERY_NOTE_NOT_REGISTERED", "Delivery note not registered.");
            case "note_already_invoiced": return MIOLocalizeString("DELIVERY_NOTE_ALREADY_INVOICED", "Delivery note already invoiced.");
            case "note_amount_differs": return MIOLocalizeString("DELIVERY_NOTE_AMOUNT_DIFFERS", "Delivery note amount differs.");
            case "duplicate": return MIOLocalizeString("POSSIBLE_DUPLICATE", "Possible duplicate.");
            case "supplier_unknown": return MIOLocalizeString("SUPPLIER_NOT_FOUND", "Supplier not found.");
            case "supplier_matched_by_name": return MIOLocalizeString("SUPPLIER_MATCHED_BY_NAME", "Supplier matched by name only.");
            case "supplier_from_record": return MIOLocalizeString("SUPPLIER_TAKEN_FROM_THE_UPLOAD", "Supplier taken from the upload.");
            case "supplier_record_differs": return MIOLocalizeString("SUPPLIER_DIFFERS_FROM_THE_UPLOAD", "Supplier differs from the upload.");
            case "addressed_to_other": return MIOLocalizeString("ADDRESSED_TO_ANOTHER_BUSINESS", "Addressed to another business.");
            case "format_changed": return MIOLocalizeString("SUPPLIER_LAYOUT_CHANGED", "The supplier's layout changed.");
            case "order_not_delivered": case "order_quantity": case "order_unordered": return MIOLocalizeString("DIFFERS_FROM_THE_ORDER", "Differs from the order.");
        }
        let text = String(code ?? "").replace(/_/g, " ");
        return text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) + "." : MIOLocalizeString("TO_REVIEW", "To review.");
    }

    private renderIssues(issues:{ severity:string, title:string, detail:string }[]){
        this.issuesView.hidden = issues.length == 0;
        if (issues.length == 0) return;
        this.issuesTitleLabel.text = issues.length + " " + (issues.length == 1 ? MIOLocalizeString("ISSUE_TO_REVIEW", "issue to review") : MIOLocalizeString("ISSUES_TO_REVIEW", "issues to review"));
        let html = "";
        for (let i of issues) {
            html += '<div class="dx-issue"><div class="dx-dot' + (i.severity == "warning" ? " warning" : "") + '"></div><div><span class="dx-issue-title">' + i.title + '</span> <span class="dx-issue-detail">' + i.detail + '</span></div></div>';
        }
        (this.issuesListView.layer as HTMLElement).innerHTML = html;
    }

    // 3. the totals read against what the lines on the document add up to now

    private renderTotals(ex:any){
        let esc = DocumentExtractionLineCell.escape;
        let money = this.money;
        let totals = ex["totals"] ?? {};
        let now = this.linesTotals();
        let hasReading = totals["total"] != null || totals["base"] != null;
        this.totalsView.hidden = !hasReading;
        if (!hasReading) return;

        let cell = (doc:number, current:number) => {
            let diff = doc != null ? doc - current : null;
            let diffText = diff == null ? "-" : (Math.abs(diff) < 0.005 ? "—" : '<span class="dx-diff">' + money(diff) + "</span>");
            return '<div class="dx-num">' + (doc != null ? money(doc) : "-") + '</div><div class="dx-num">' + money(current) + '</div><div class="dx-num">' + diffText + "</div>";
        };
        let html = '<div class="dx-row dx-head"><div></div><div class="dx-num">' + MIOLocalizeString("ON_DOCUMENT", "On document") + '</div><div class="dx-num">' + MIOLocalizeString("IN_INVOICE_NOW", "In invoice now") + '</div><div class="dx-num">' + MIOLocalizeString("DIFFERENCE", "Difference") + "</div></div>";
        html += '<div class="dx-row"><div>' + MIOLocalizeString("BASE", "Base") + "</div>" + cell(this.number(totals["base"]), now.base) + "</div>";

        let summary = (ex["taxSummary"] ?? []) as any[];
        if (summary.length == 0 && totals["tax"] != null) {
            let base = this.number(totals["base"]);
            let tax = this.number(totals["tax"]);
            let implied = base != null && base > 0 && tax != null ? Math.round(tax / base * 10000) / 100 : null;
            summary = [{ "rate": implied, "amount": tax }];
        }
        for (let t of summary) {
            let rate = t["rate"];
            let known = rate != null && this.session.venueTaxForRate(rate) != null;
            let label = "IVA " + (known ? esc(rate) + "%" : '<span class="dx-rate-unknown">' + (rate != null ? esc(rate) : "?") + "%</span>");
            let current = rate != null ? now.taxByRate[String(Number(rate))] ?? 0 : 0;
            html += '<div class="dx-row"><div>' + label + "</div>" + cell(this.number(t["amount"]), current) + "</div>";
        }
        if (this.number(totals["withholding"]) != null && this.number(totals["withholding"]) != 0) {
            html += '<div class="dx-row"><div>' + MIOLocalizeString("WITHHOLDING", "Withholding") + "</div>" + cell(-this.number(totals["withholding"]), -now.legalTax) + "</div>";
        }
        html += '<div class="dx-row dx-total"><div>' + MIOLocalizeString("TOTAL", "Total") + "</div>" + cell(this.number(totals["total"]), now.total) + "</div>";
        (this.totalsTableView.layer as HTMLElement).innerHTML = html;
    }

    /// What the lines on the document add up to now: base, VAT by rate, withholding-like taxes, total.
    private linesTotals() : { base:number, total:number, legalTax:number, taxByRate:{[rate:string]:number} } {
        let result = { base: 0, total: 0, legalTax: 0, taxByRate: {} as {[rate:string]:number} };
        let document = this.session.document as any;
        for (let l of (document?.lines?.allObjects ?? []) as any[]) {
            if (l instanceof DBDocumentTitleLine) continue;
            result.base += Number(l.baseAmount ?? 0);
            result.total += Number(l.totalAmount ?? 0);
            result.legalTax += Number(l.legalTaxAmount ?? 0);
            let vat = l.vat as Tax;
            if (vat != null && vat.taxQuantity != null) {
                let rate = String(Math.round(vat.taxQuantity * 10000) / 100);
                result.taxByRate[rate] = (result.taxByRate[rate] ?? 0) + Number(l.vatAmount ?? 0);
            }
        }
        return result;
    }

    // 4. the detected lines with their match

    private renderLines(ex:any){
        let esc = DocumentExtractionLineCell.escape;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let nf = ad.numberFormatter;
        let money = this.money;
        let rows = this.session.allRows;
        this.linesView.hidden = false;
        this.linesTitleLabel.text = MIOLocalizeString("DETECTED_LINES", "DETECTED LINES") + " · " + rows.length;

        let html = '<div class="dx-row dx-head"><div>' + MIOLocalizeString("DESCRIPTION", "Description") + '</div><div class="dx-num">' + MIOLocalizeString("QTY", "Qty") + '</div><div class="dx-num">' + MIOLocalizeString("PRICE", "Price") + '</div><div class="dx-num">' + MIOLocalizeString("TOTAL", "Total") + "</div></div>";
        if (rows.length == 0) html += '<div class="dx-row dx-empty"><div>' + MIOLocalizeString("NO_LINES_READ", "No lines read.") + "</div><div></div><div></div><div></div></div>";
        for (let r of rows) {
            let line = r.line ?? {};
            let status:string, cls:string;
            if (r.applied) { status = MIOLocalizeString("ADDED", "Added"); cls = "dx-quiet"; }
            else if (r.notAProduct) { status = MIOLocalizeString("NOT_A_PRODUCT", "Not a product"); cls = "dx-quiet"; }
            else if (r.product != null) { status = MIOLocalizeString("LINKED", "Linked") + ": " + esc(r.product.name); cls = "dx-linked"; }
            else if (r.conceptItem != null) { status = MIOLocalizeString("CONCEPT", "Concept") + ": " + esc(r.conceptItem.name); cls = "dx-linked"; }
            else { status = MIOLocalizeString("UNKNOWN_PRODUCT", "Unknown product"); cls = ""; }
            let quantity = this.number(line["quantity"]);
            let qty = quantity != null ? nf.stringFromNumber(quantity) + (line["unit"] ? " " + esc(line["unit"]) : "") : "";
            let price = this.number(line["unitPrice"]);
            let total = this.number(line["total"] ?? line["base"]);
            html += '<div class="dx-row"><div class="dx-desc"><div class="dx-text">' + esc(line["description"] ?? "") + '</div><div class="dx-status ' + cls + '">' + status + "</div></div>"
                  + '<div class="dx-num">' + qty + '</div><div class="dx-num">' + (price != null ? money(price) : "") + '</div><div class="dx-num">' + (total != null ? money(total) : "") + "</div></div>";
        }
        (this.linesTableView.layer as HTMLElement).innerHTML = html;
    }

    // MARK: helpers

    private number(value:any) : number {
        if (value == null || value === "") return null;
        let n = Number(value);
        return isNaN(n) ? null : n;
    }

    private money = (value:number) : string => {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        return value != null && !isNaN(value) ? ad.currencyFormatter.stringFromNumber(value) : "-";
    }
}
